import classNames from 'classnames';
import type { SwitchSize } from './Switch.types';

type SwitchState = 'on' | 'off';
type SwitchSlots = 'root' | 'wrapper' | 'circle';

const baseInteractivityClassName =
  'cursor-pointer focus-visible:outline-none focus-visible:ring hover:bg-state-hover active:bg-state-press';

const enabledRootClassName: { [Key in SwitchState]: string } = {
  on: `${baseInteractivityClassName}  border-shade-10 bg-element-secondary`,
  off: `${baseInteractivityClassName}  border-shade-10 bg-plain`,
};

const errorRootClassName: { [Key in SwitchState]: string } = {
  on: `${baseInteractivityClassName}  border-shade-10 bg-status-error`,
  off: `${baseInteractivityClassName}  border-status-error bg-plain`,
};

const disabledRootClassName: { [Key in SwitchState]: string } = {
  on: 'pointer-events-none text-plain bg-status-disabled border-shade-10',
  off: 'pointer-events-none text-status-disabled-dark bg-status-disabled border-status-disabled-dark',
};

const sizeClassName: { [Key in SwitchSlots]: { [Key in SwitchSize]: string } } =
  {
    root: {
      small: 'w-8 h-4',
      default: 'w-10 h-5',
      large: 'w-12 h-6',
    },
    wrapper: {
      small: 'w-7 h-3',
      default: 'w-9 h-4',
      large: 'w-11 h-5',
    },
    circle: {
      small: 'size-3',
      default: 'size-4',
      large: 'size-5',
    },
  };

type SwitchClassNames = { [K in SwitchSlots]: string };

export const getSwitchClassNames = (
  value: boolean,
  size: SwitchSize = 'default',
  disabled?: boolean,
  hasError?: boolean,
  className?: string,
): SwitchClassNames => {
  const state = value ? 'on' : 'off';

  return {
    root: classNames(
      'border border-solid rounded-full',
      'inline-flex items-center justify-center',
      disabled
        ? disabledRootClassName[state]
        : hasError
          ? errorRootClassName[state]
          : enabledRootClassName[state],
      sizeClassName.root[size],
      className,
    ),
    wrapper: classNames(
      'inline-flex items-center',
      value ? 'justify-end' : 'justify-start',
      sizeClassName.wrapper[size],
    ),
    circle: classNames(
      'rounded-full',
      disabled ? 'bg-status-disabled-dark' : 'bg-default',
      sizeClassName.circle[size],
    ),
  };
};
